'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Call, CallCalling } from 'iconsax-react';
import useVerifyUser from '@/hooks/profile/useVerifyUser';
import LoginFormComponent from '@/components/(dashboard)/auth/LoginFormComponent';
import { parseCookies } from 'nookies';
import { notifyLandingRegister } from '@/utils/profile/landing-register/notifyLandingRegister';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { TemplateLandingACF } from '@/types/post/slug';
import CloseFillIcon from 'remixicon-react/CloseFillIcon';
import SectionImage from '@/components/(landing)/landing/SectionImage';
import { Input } from '@/components/ui/input';
import { updateUser as updateUserRequest } from '@/utils/profile/updateUser';

type CTAComponentProps = {
    data: TemplateLandingACF['ctaSection'];
    id: string;
};

const CTAComponent: React.FC<CTAComponentProps> = ({ data, id }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { isLoggedInGlobal, mobile, isLoading, user } = useVerifyUser();
    const { ctaTitle, ctaSubtitle, callButton, redirectButton } = data;
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [hasRegistered, setHasRegistered] = useState(false);
    const [hasCheckedLogin, setHasCheckedLogin] = useState(false);
    const [hasScrolledEnough, setHasScrolledEnough] = useState(false);
    const [isCTAVisible, setIsCTAVisible] = useState(false);
    const [urlWithoutProtocol, setUrlWithoutProtocol] = useState('');
    const [phone, setPhone] = useState<string>(user?.mobile ?? '');
    const [fullName, setFullName] = useState<string>(user?.firstName ?? '');
    const [email, setEmail] = useState<string>(user?.email ?? '');

    const ctaRef = useRef<HTMLSpanElement | null>(null);
    const isButtonVisible = hasScrolledEnough && !isCTAVisible;
    useEffect(() => {
        // Ensure this runs only in the browser
        if (typeof window !== 'undefined') {
            setUrlWithoutProtocol(window.location.href);
        }
    }, []);
    useEffect(() => {
        setPhone(user?.mobile ?? '');
        setFullName(user?.firstName ?? '');
        setEmail(user?.email ?? '');
    }, [user]);
    const { toast } = useToast();

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY || window.pageYOffset;
            setHasScrolledEnough(scrollTop > 100);
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const target = ctaRef.current;
        if (!target) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries.find((e) => e.target === target);
                if (entry) {
                    setIsCTAVisible(entry.isIntersecting);
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(target);

        return () => {
            observer.unobserve(target);
        };
    }, []);

    useEffect(() => {
        if (!isLoading) setHasCheckedLogin(true);
    }, [isLoading]);

    useEffect(() => {
        if (isEditing) setHasRegistered(false);
    }, [isEditing]);

    const handleScrollToCTA = () => {
        const target = document.getElementById('cta-section');
        if (target) target.scrollIntoView({ behavior: 'smooth' });
    };

    const handleNotify = async () => {
        setLoading(true);
        try {
            const token = parseCookies()['user_auth_token'];
            if (!token) {
                toast({
                    className: 'bg-error/95 text-white rounded-2xl font-light border-0',
                    title: 'توکن کاربر پیدا نشد.',
                });
                return;
            }
            await updateUserRequest({ fullName, email });
            const response = await notifyLandingRegister(
                phone,
                token,
                'nokhbegan',
                urlWithoutProtocol,
            );
            if (response?.success) {
                toast({
                    className: 'bg-success/95 text-white rounded-2xl font-light border-0',
                    title: response.message || 'ثبت نام با موفقیت انجام شد.',
                });
                setHasRegistered(true);
            } else {
                toast({
                    className: 'bg-error/95 text-white rounded-2xl font-light border-0',
                    title: response?.message || 'خطا در ارسال اطلاعات.',
                });
            }
        } catch (error) {
            console.error('Notification failed', error);
            toast({
                className: 'bg-error/95 text-white rounded-2xl font-light border-0',
                title: 'خطا در ارسال اطلاعات.',
            });
        } finally {
            setLoading(false);
        }
    };
    const showPopUp = false;
    return (
        <>
            {showPopUp && (
                <div className={'hidden'}>
                    <div
                        onClick={() => setModalOpen(true)}
                        className={
                            'cursor-pointer fixed bottom-32 z-10 start-5 flex gap-x-2 items-center rounded-[20px] bg-white hover:scale-105 transition-all p-1 pe-3 shadow-sm'
                        }
                    >
                        <div className={'flex items-center'}>
                            {data.operators.map((operator, index) => (
                                <Image
                                    src={operator?.sourceUrl || ''}
                                    className={`${index > 0 ? '-ms-3' : ''} size-10 rounded-full border-2 border-white`}
                                    width={1800}
                                    height={1800}
                                    alt={operator?.title}
                                    key={index}
                                />
                            ))}
                        </div>
                        <div className={'text-[11px]'}>
                            <p>سلام👋</p>
                            <p>سوالی دارید از پشتیبانی بپرسید</p>
                        </div>
                    </div>
                    <div
                        onClick={() => setModalOpen(false)}
                        className={`bg-black/80  backdrop-blur-lg transition-opacity duration-500 fixed inset-0 ${modalOpen ? 'z-[49]' : 'opacity-0 z-[-1]'}`}
                    ></div>
                    <div
                        className={`fixed bg-gray-100 dark:bg-primary-dark border border-b-0 border-primary-hover dark:border-white/20 z-50 lg:start-3 start-1/2 max-lg:translate-x-1/2 bottom-0 overflow-hidden prose-p:!text-white w-11/12 lg:w-[350px] h-[530px] rounded-t-2xl duration-500 origin-right transition-all ${modalOpen ? 'translate-y-0' : 'translate-y-full scale-x-75'}`}
                    >
                        <div
                            className={
                                'flex justify-between items-center bg-primary-hover dark:bg-primary-dark p-3 border-b dark:border-white/20'
                            }
                        >
                            <div className={'flex items-center'}>
                                {data.operators.map((operator, index) => (
                                    <Image
                                        src={operator?.sourceUrl || ''}
                                        className={`${index > 0 ? '-ms-3 grayscale' : ''} size-8 rounded-full border-2 border-white`}
                                        width={1800}
                                        height={1800}
                                        alt={operator?.title}
                                        key={index}
                                    />
                                ))}
                            </div>
                            <button onClick={() => setModalOpen(false)}>
                                <CloseFillIcon
                                    size={27}
                                    className={'text-white hover:scale-110 transition-all'}
                                />
                            </button>
                        </div>
                        <div
                            className={
                                'p-2 bg-gray-100 dark:bg-white/10 pt-5 h-full flex flex-col justify-between'
                            }
                        >
                            <div className={' h-[100px] p-3'}>
                                <div className={'flex gap-x-2 items-start w-2/3'}>
                                    <div className={'relative shrink-0'}>
                                        <Image
                                            width={1200}
                                            height={1200}
                                            className={
                                                'rounded-full size-9 aspect-square object-cover'
                                            }
                                            src={data?.operators?.[0]?.sourceUrl || ''}
                                            alt={data?.operators?.[0]?.title || 'operator-1'}
                                        />
                                        <span className='inline-flex absolute bottom-0 end-1 size-2 rounded-full bg-[#13C783] opacity-75'>
                                            <span className='inline-flex size-2 animate-ping rounded-full bg-[#13C783] opacity-75'></span>
                                        </span>
                                    </div>
                                    <div
                                        className={
                                            'bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 text-black/80 dark:text-white/80 rounded-2xl rounded-tr-none mt-5 text-[11px] p-2'
                                        }
                                    >
                                        برای شروع گفتگو شماره تماس خود را وارد کنید.
                                    </div>
                                </div>
                            </div>
                            <div className={'flex flex-col flex-grow justify-center'}>
                                {isLoading || !hasCheckedLogin || loading ? (
                                    <div className='flex justify-center items-center min-h-[150px]'>
                                        <Spinner />
                                    </div>
                                ) : isLoggedInGlobal ? (
                                    hasRegistered ? (
                                        <div className='p-5 rounded-2xl text-center '>
                                            <h2 className='text-xl font-bold mb-2'>
                                                ثبت نام موفقیت‌آمیز
                                            </h2>
                                            <span className='text-black dark:text-white text-xs  mb-4'>
                                                مراحل عضویت برای شما پیامک می‌شود و پشتیبان با شما
                                                تماس خواهند گرفت.
                                            </span>
                                            <Link
                                                href={redirectButton}
                                                className='w-1/2 mx-auto bg-success text-white py-2 rounded-xl flex justify-center'
                                            >
                                                تکمیل ثبت نام
                                            </Link>
                                        </div>
                                    ) : isEditing ? (
                                        <div className='flex flex-col prose-p:!text-white items-center gap-4 text-white rounded-2xl p-2 w-full'>
                                            <LoginFormComponent
                                                title=' '
                                                httpReferrer={urlWithoutProtocol}
                                                textBtn='ارسال درخواست مشاوره'
                                                landingSlug='nokhbegan'
                                                handleLoginSuccess={() => {
                                                    setHasRegistered(true);
                                                    setIsEditing(false);
                                                }}
                                            />
                                            <button
                                                onClick={() => setIsEditing(false)}
                                                className='underline text-black dark:text-white pb-10'
                                            >
                                                انصراف و بازگشت
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='mt-10 text-black dark:!text-white text-xs text-center'>
                                                شما با شماره موبایل{' '}
                                                <span className='font-bold underline decoration-2 decoration-white'>
                                                    {mobile}
                                                </span>{' '}
                                                در حال ثبت نام هستید.
                                            </div>
                                            <button
                                                onClick={() => setIsEditing(true)}
                                                className='underline dark:text-white my-4'
                                            >
                                                ویرایش شماره
                                            </button>
                                            <button
                                                onClick={handleNotify}
                                                disabled={loading}
                                                className='bg-primary-hover dark:bg-white ms-3 text-white dark:text-primary hover:brightness-125 transition-all py-2 px-4 rounded-xl font-bold'
                                            >
                                                ثبت نام در دوره نخبگان
                                            </button>
                                        </>
                                    )
                                ) : (
                                    <div className='flex justify-center w-full'>
                                        <LoginFormComponent
                                            title=' '
                                            textBtn='ارسال درخواست مشاوره'
                                            httpReferrer={urlWithoutProtocol}
                                            landingSlug='nokhbegan'
                                            handleLoginSuccess={() => setHasRegistered(true)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/*old code */}
            <section id={id} ref={ctaRef} className='bg-primary-dark pt-10 pb-20'>
                <div className='max-w-xl mx-auto max-lg:px-0 flex flex-col items-center justify-center'>
                    <div className={'w-full flex items-center justify-center gap-2'}>
                        {data.operators.map((operator, index) => (
                            <Image
                                src={operator?.sourceUrl || ''}
                                loading='lazy'
                                className={`${index === 1 ? 'scale-150' : ''} size-24 lg:size-32 rounded-full border-4 border-primary-dark`}
                                width={200}
                                height={200}
                                alt={operator?.title}
                                key={index}
                            />
                        ))}
                    </div>
                    <div className='relative w-full max-lg:w-11/12 p-9 max-lg:px-3 mt-12 bg-primary-active rounded-2xl text-white text-center'>
                        <SectionImage
                            imageUrl={'/nokhbegan/13-cta-section.webp'}
                            imageTitle={'cta-section'}
                        />
                        <h3 className='text-2xl hidden font-bold mb-4 pt-5'>{ctaTitle}</h3>
                        <div className='mt-1 hidden mb-5 text-sm'>{ctaSubtitle}</div>

                        {isLoading || !hasCheckedLogin || loading ? (
                            <div className='flex justify-center items-center min-h-[150px]'>
                                <Spinner />
                            </div>
                        ) : isLoggedInGlobal ? (
                            hasRegistered ? (
                                <div className='p-5 rounded-2xl mt-6 text-center'>
                                    <h2 className='text-2xl font-bold mb-2'>ثبت نام موفقیت‌آمیز</h2>
                                    <p className='!text-white mb-4 lg:text-sm text-xs'>
                                        مراحل عضویت برای شما پیامک می‌شود و پشتیبان با شما تماس
                                        خواهند گرفت.
                                    </p>
                                    <Link
                                        href={redirectButton}
                                        className='lg:w-1/2 w-full mx-auto bg-landing-gold text-primary py-2 hover:brightness-110 rounded-xl flex justify-center'
                                    >
                                        تکمیل ثبت نام
                                    </Link>
                                </div>
                            ) : isEditing ? (
                                <div className='flex flex-col items-center gap-2 rounded-2xl w-full mt-4'>
                                    <LoginFormComponent
                                        title=' '
                                        httpReferrer={urlWithoutProtocol}
                                        textBtn='درخواست مشاوره'
                                        newStyle={true}
                                        popupWizardStyle={true}
                                        landingSlug='nokhbegan'
                                        handleLoginSuccess={() => {
                                            setHasRegistered(true);
                                            setIsEditing(false);
                                        }}
                                    />
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className='border border-white px-5 py-2 text-xs rounded-xl text-white hover:bg-white hover:text-primary transition-all'
                                    >
                                        انصراف و بازگشت
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <p className='my-6 dark:!text-white max-lg:text-xs'>
                                        شما با شماره موبایل{' '}
                                        <span className='font-bold underline decoration-2 decoration-white'>
                                            {mobile}
                                        </span>{' '}
                                        در حال ثبت نام هستید.
                                    </p>
                                    <div
                                        className={
                                            'w-11/12 lg:w-2/3 mx-auto flex flex-col gap-3 justify-center items-center mb-5'
                                        }
                                    >
                                        <Input
                                            className={`w-full border group border-white/15 py-5 px-4 text-white z-10 focus-visible:outline-0 shadow-none !text-sm relative rounded-xl`}
                                            type='text'
                                            required
                                            value={fullName}
                                            placeholder={'نام خود را وارد کنید!'}
                                            onChange={(e) => setFullName(e.target.value)}
                                            id='fullname'
                                        />
                                        <Input
                                            className={`w-full border group border-white/15 py-5 px-4 text-white z-10 focus-visible:outline-0 shadow-none !text-sm relative rounded-xl`}
                                            type='email'
                                            required
                                            value={email}
                                            placeholder={'ایمل خود را وارد کنید!'}
                                            onChange={(e) => setEmail(e.target.value)}
                                            id='userEmail'
                                        />
                                    </div>
                                    <div
                                        className={
                                            'w-11/12 lg:w-2/3 flex gap-2 mx-auto justify-center'
                                        }
                                    >
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className='border border-white/5 text-nowrap z-0 shrink-0 hover:bg-white/15 transition-all text-white/75 bg-white/10 p-2 text-xs rounded-xl'
                                        >
                                            ویرایش شماره
                                        </button>
                                        <button
                                            disabled={fullName === '' || email === '' || loading}
                                            onClick={handleNotify}
                                            className={`bg-landing-gold relative w-full text-nowrap hover:brightness-110 text-primary py-2 lg:px-10 px-5 rounded-xl font-bold ${fullName === '' || email === '' ? 'opacity-50' : ''}`}
                                        >
                                            ثبت نام در دوره نخبگان
                                        </button>
                                    </div>
                                </>
                            )
                        ) : (
                            <div className='flex justify-center w-11/12 lg:w-2/3 mx-auto rounded-2xl mt-4'>
                                <LoginFormComponent
                                    title=' '
                                    textBtn='درخواست مشاوره'
                                    newStyle={true}
                                    popupWizardStyle={true}
                                    httpReferrer={urlWithoutProtocol}
                                    landingSlug='nokhbegan'
                                    handleLoginSuccess={() => setHasRegistered(true)}
                                />
                            </div>
                        )}

                        <Link
                            className='bg-white mt-7 mx-auto lg:px-12 flex w-11/12 lg:w-2/3 py-3 rounded-2xl text-primary hover:brightness-90 transition-all text-center justify-center items-center font-bold gap-2'
                            href={callButton.url}
                        >
                            {callButton.title}
                            <Call size={20} className='stroke-primary animate-pulse' />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Sticky Button */}
            {showPopUp && (
                <div
                    onClick={handleScrollToCTA}
                    className={`fixed hidden max-lg:w-full lg:w-[500px] lg:mx-auto bottom-0 right-0 left-0 z-20 container bg-white rounded-t-xl lg:p-3 p-2 transition-all duration-500 ${
                        isButtonVisible
                            ? 'translate-y-0 opacity-100 pointer-events-auto'
                            : 'translate-y-full opacity-0 pointer-events-none'
                    }`}
                >
                    <div className='flex justify-between items-stretch mb-2'>
                        <div className='flex flex-col text-xs dark:text-primary'>
                            <div className='mb-2'>
                                <span className='font-bold'>۹۵٪</span> از ظرفیت ثبت نام تکمیل شده
                            </div>
                            <Progress value={15} className='h-[6px] bg-primary rounded-full' />
                        </div>
                        <div className='bg-success flex items-center rounded-lg px-3 py-1 text-white gap-2'>
                            <Link href={callButton.url} id={'callButtonCta'}>
                                02191007590
                            </Link>
                            <CallCalling size={20} variant='Bold' className='stroke-white' />
                        </div>
                    </div>
                    <button className='bg-landing-gold p-3 rounded-2xl w-full font-bold text-lg'>
                        رزرو وقت مشاوره
                    </button>
                </div>
            )}
        </>
    );
};

export default CTAComponent;
